import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const Stars = ({ rating, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        className={`w-4 h-4 ${s <= rating ? "text-[#C29B62]" : "text-[#F7F5F0]/25"}`}
        fill={s <= rating ? "currentColor" : "none"}
      />
    ))}
  </div>
);

const byline = (r) =>
  [r.guest_name, r.source, r.date].filter(Boolean).join(" · ");

export const Reviews = ({ t }) => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((res) => setReviews(res.data)).catch(() => {});
  }, []);

  if (!reviews.length) return null;
  const [featured, ...rest] = reviews;

  return (
    <section id="recensioni" data-testid="reviews-section" className="py-24 lg:py-32 bg-[#1C3829]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="max-w-2xl"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-[#C29B62]">{t.reviews.overline}</p>
          <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight text-[#F7F5F0]">{t.reviews.title}</h2>
          <p className="mt-6 text-base text-[#F7F5F0]/70 font-light leading-relaxed">{t.reviews.sub}</p>
        </motion.div>

        <motion.figure
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-16 max-w-4xl"
          data-testid="review-featured"
        >
          <Quote className="w-8 h-8 text-[#C29B62]" fill="currentColor" />
          <blockquote className="mt-6 font-serif-display text-2xl sm:text-3xl font-light leading-snug text-[#F7F5F0]">
            {featured.text}
          </blockquote>
          <figcaption className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3">
            <Stars rating={featured.rating} testid="review-featured-stars" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#C29B62]">{byline(featured)}</span>
          </figcaption>
        </motion.figure>

        {rest.length > 0 && (
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="reviews-grid">
            {rest.map((r, i) => (
              <motion.article
                key={r.id}
                data-testid={`review-card-${i}`}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: (i % 3) * 0.12 }}
                className="border border-[#C29B62]/25 bg-[#F7F5F0]/[0.04] p-8 flex flex-col hover:border-[#C29B62]/50 transition-colors"
              >
                <Stars rating={r.rating} testid={`review-stars-${i}`} />
                <p className="mt-5 text-base font-light leading-relaxed text-[#F7F5F0]/85 italic flex-1">
                  &ldquo;{r.text}&rdquo;
                </p>
                <p className="mt-6 text-xs uppercase tracking-[0.25em] text-[#C29B62]">{byline(r)}</p>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
